import { type ChangeEvent, type RefObject, useState } from "react";
import styled from "styled-components";

import { PageBody } from "../PageBody";
import { Label } from "../../atoms/Label";
import { Input } from "../../atoms/Input";
import { CheckInput } from "../../atoms/CheckInput";
import { Button } from "../../atoms/Button";
import { Typography } from "../../atoms/Typography";
import { NotificationBody } from "../../organisms/notification/NotificationBody";
import { Loading } from "../../atoms/Loading";
import { MarkdownHelp } from "../../organisms/notification/MarkdownHelp";

const ContentWrapper = styled.div`
  padding: 20px 40px;
`;

const FieldGroup = styled.div`
  margin-bottom: 24px;
`;

const TabContainer = styled.div`
  border: 1px solid ${(props) => props.theme.colors.primaryBorder};
  border-radius: 6px;
  overflow: hidden;
`;

const TabHeader = styled.div`
  display: flex;
  border-bottom: 1px solid ${(props) => props.theme.colors.primaryBorder};
  background-color: ${(props) => props.theme.colors.pageBodyBackground};
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 8px 16px;
  border: none;
  background: ${(props) =>
    props.$active ? props.theme.colors.primaryBackground : "transparent"};
  border-bottom: ${(props) =>
    props.$active
      ? `2px solid ${props.theme.colors.brand}`
      : "2px solid transparent"};
  cursor: pointer;
  font-size: ${(props) => props.theme.fontSize.xsmall};
  color: ${(props) => props.theme.colors.primaryText};
  font-weight: ${(props) => (props.$active ? "bold" : "normal")};
`;

const TabBody = styled.div`
  padding: 16px;
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  min-height: 300px;
  padding: 12px;
  border: 1px solid ${(props) => props.theme.colors.primaryBorder};
  border-radius: 6px;
  background-color: ${(props) => props.theme.colors.inputFieldBackground};
  color: ${(props) => props.theme.colors.primaryText};
  font-size: ${(props) => props.theme.fontSize.small};
  font-family: monospace;
  resize: vertical;
  outline: 0;
  box-sizing: border-box;
`;

const ImageUploadWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
`;

const ImageUploadButton = styled.button`
  padding: 6px 12px;
  border: 1px solid ${(props) => props.theme.colors.primaryBorder};
  border-radius: 4px;
  background: ${(props) => props.theme.colors.primaryBackground};
  cursor: pointer;
  font-size: ${(props) => props.theme.fontSize.xsmall};
  color: ${(props) => props.theme.colors.primaryText};
  &:hover {
    background: ${(props) => props.theme.colors.hoverBackground};
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const MedicalInstitutionRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const AddButton = styled.button`
  height: 44px;
  padding: 0 16px;
  border: 1px solid ${(props) => props.theme.colors.primaryBorder};
  border-radius: 6px;
  background: ${(props) => props.theme.colors.primaryBackground};
  cursor: pointer;
  font-size: ${(props) => props.theme.fontSize.xsmall};
  flex-shrink: 0;
  &:hover {
    background: ${(props) => props.theme.colors.hoverBackground};
  }
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: ${(props) => props.theme.colors.pageBodyBackground};
  border: 1px solid ${(props) => props.theme.colors.primaryBorder};
  border-radius: 4px;
  font-size: ${(props) => props.theme.fontSize.xsmall};
`;

const RemoveButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  padding: 0 2px;
  font-size: 14px;
  color: ${(props) => props.theme.colors.primaryText};
`;

const CheckboxGroup = styled.div`
  display: flex;
  gap: 4px 16px;
  flex-wrap: wrap;
  & label {
    display: inline-flex;
    align-items: center;
  }
`;

const CancelDivider = styled.hr`
  border: none;
  border-top: 1px solid ${(props) => props.theme.colors.secondaryBorder};
  width: 100%;
  margin: 0;
`;

const CancelLink = styled.div`
  font-size: ${(props) => props.theme.fontSize.xsmall};
  color: ${(props) => props.theme.colors.primaryText};
  cursor: pointer;
  text-decoration: underline;
  display: inline-block;
  margin-top: 12px;
`;

const DateRow = styled.div`
  display: flex;
  gap: 24px;
`;

const DateField = styled.div`
  width: 300px;
`;

const DateInput = styled.input`
  width: 100%;
  padding: 13px 10px;
  border: 1px solid ${(props) => props.theme.colors.primaryBorder};
  border-radius: 6px;
  background-color: ${(props) => props.theme.colors.inputFieldBackground};
  color: ${(props) => props.theme.colors.primaryText};
  font-size: ${(props) => props.theme.fontSize.small};
  outline: 0;
`;

const SubmitArea = styled.div`
  margin-top: 32px;
  margin-bottom: 20px;
  text-align: right;
`;

const SubmitWrapper = styled.div`
  width: 200px;
  height: 44px;
  display: inline-block;
  margin-bottom: 12px;
`;

const PreviewArea = styled.div`
  min-height: 200px;
`;

type ActiveTab = "edit" | "preview";

export type NotificationCreatePageProps = {
  title: string;
  body: string;
  previewHtml: string;
  medicalInstitutionIds: string[];
  medicalInstitutionIdInput: string;
  roles: string[];
  positions: string[];
  pdtCodes: string[];
  startFrom: string;
  endAt: string;
  isSubmitting: boolean;
  isUploading: boolean;
  isPreviewLoading: boolean;
  fileInputRef: RefObject<HTMLInputElement | null>;
  onTitleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onBodyChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onMedicalInstitutionIdInputChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onAddMedicalInstitutionId: () => void;
  onRemoveMedicalInstitutionId: (id: string) => void;
  onRoleToggle: (role: string) => void;
  onPositionToggle: (position: string) => void;
  onPdtCodeToggle: (code: string) => void;
  onStartFromChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onEndAtChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onImageUploadClick: () => void;
  onFileSelect: (e: ChangeEvent<HTMLInputElement>) => void;
  onPreviewTab: () => void;
  onSubmit: () => void;
  onCancel: () => void;
};

const ROLE_OPTIONS = [
  { value: "ADMIN", label: "管理者" },
  { value: "STAFF", label: "スタッフ" },
];

const POSITION_OPTIONS = [
  { value: "DOCTOR", label: "医師" },
  { value: "NURSE", label: "看護師" },
  { value: "STAFF", label: "スタッフ" },
];

const PDT_CODE_OPTIONS = [
  { value: "2184b7b9-2dba-4a97-8fff-ccd7d0c8c40d", label: "SC" },
  { value: "39dd9c55-343f-4810-ba8a-f417b07d6caf", label: "HT" },
  { value: "c1a2b5e0-c0a3-41c3-aadd-7e14043fedb3", label: "RELIVION" },
  { value: "d38a1261-c952-41a7-8333-251498ad2d7f", label: "HAUDY" },
];

export function NotificationCreatePage({
  title,
  body,
  previewHtml,
  medicalInstitutionIds,
  medicalInstitutionIdInput,
  roles,
  positions,
  pdtCodes,
  startFrom,
  endAt,
  isSubmitting,
  isUploading,
  isPreviewLoading,
  fileInputRef,
  onTitleChange,
  onBodyChange,
  onMedicalInstitutionIdInputChange,
  onAddMedicalInstitutionId,
  onRemoveMedicalInstitutionId,
  onRoleToggle,
  onPositionToggle,
  onPdtCodeToggle,
  onStartFromChange,
  onEndAtChange,
  onImageUploadClick,
  onFileSelect,
  onPreviewTab,
  onSubmit,
  onCancel,
}: NotificationCreatePageProps) {
  const [activeTab, setActiveTab] = useState<ActiveTab>("edit");

  const handleTabChange = (tab: ActiveTab) => {
    setActiveTab(tab);
    if (tab === "preview") {
      onPreviewTab();
    }
  };

  return (
    <PageBody title="お知らせ作成">
      <ContentWrapper>
        <FieldGroup>
          <Label text="タイトル" emphasis="required" />
          <Input
            value={title}
            onChange={onTitleChange}
            placeholder="お知らせのタイトル"
          />
        </FieldGroup>

        <FieldGroup>
          <Label text="本文（Markdown）" emphasis="required" />
          <TabContainer>
            <TabHeader>
              <Tab
                $active={activeTab === "edit"}
                onClick={() => handleTabChange("edit")}
              >
                編集
              </Tab>
              <Tab
                $active={activeTab === "preview"}
                onClick={() => handleTabChange("preview")}
              >
                プレビュー
              </Tab>
            </TabHeader>
            <TabBody>
              {activeTab === "edit" ? (
                <>
                  <ImageUploadWrapper>
                    <ImageUploadButton
                      onClick={onImageUploadClick}
                      disabled={isUploading}
                    >
                      {isUploading ? "アップロード中..." : "画像を挿入"}
                    </ImageUploadButton>
                  </ImageUploadWrapper>
                  <HiddenFileInput
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={onFileSelect}
                  />
                  <StyledTextarea
                    value={body}
                    onChange={onBodyChange}
                    placeholder="Markdownで入力してください"
                    id="notification-body-textarea"
                  />
                  <MarkdownHelp />
                </>
              ) : (
                <PreviewArea>
                  {isPreviewLoading ? (
                    <Loading />
                  ) : previewHtml ? (
                    <NotificationBody body={previewHtml} />
                  ) : (
                    <Typography>プレビューする内容がありません</Typography>
                  )}
                </PreviewArea>
              )}
            </TabBody>
          </TabContainer>
        </FieldGroup>

        <FieldGroup>
          <Label
            text="対象医療機関（APS_ID）"
            emphasis="note"
            note="未指定 = 全医療機関"
          />
          <MedicalInstitutionRow>
            <div style={{ flex: 1 }}>
              <Input
                value={medicalInstitutionIdInput}
                onChange={onMedicalInstitutionIdInputChange}
                placeholder="医療機関ID（UUID）を入力"
              />
            </div>
            <AddButton onClick={onAddMedicalInstitutionId}>追加</AddButton>
          </MedicalInstitutionRow>
          {medicalInstitutionIds.length > 0 && (
            <TagList>
              {medicalInstitutionIds.map((id) => (
                <Tag key={id}>
                  {id}
                  <RemoveButton
                    onClick={() => onRemoveMedicalInstitutionId(id)}
                  >
                    &times;
                  </RemoveButton>
                </Tag>
              ))}
            </TagList>
          )}
        </FieldGroup>

        <FieldGroup>
          <Label text="対象製品" emphasis="note" note="未選択 = 全製品" />
          <CheckboxGroup>
            {PDT_CODE_OPTIONS.map((opt) => (
              <CheckInput
                key={opt.value}
                label={opt.label}
                value={opt.value}
                checked={pdtCodes.includes(opt.value)}
                onChange={() => onPdtCodeToggle(opt.value)}
                hideError
              />
            ))}
          </CheckboxGroup>
        </FieldGroup>

        <FieldGroup>
          <Label text="対象ロール" emphasis="note" note="未選択 = 全ロール" />
          <CheckboxGroup>
            {ROLE_OPTIONS.map((opt) => (
              <CheckInput
                key={opt.value}
                label={opt.label}
                value={opt.value}
                checked={roles.includes(opt.value)}
                onChange={() => onRoleToggle(opt.value)}
                hideError
              />
            ))}
          </CheckboxGroup>
        </FieldGroup>

        <FieldGroup>
          <Label text="対象職種" emphasis="note" note="未選択 = 全職種" />
          <CheckboxGroup>
            {POSITION_OPTIONS.map((opt) => (
              <CheckInput
                key={opt.value}
                label={opt.label}
                value={opt.value}
                checked={positions.includes(opt.value)}
                onChange={() => onPositionToggle(opt.value)}
                hideError
              />
            ))}
          </CheckboxGroup>
        </FieldGroup>

        <FieldGroup>
          <DateRow>
            <DateField>
              <Label text="掲載開始日時" emphasis="required" />
              <DateInput
                type="datetime-local"
                value={startFrom}
                onChange={onStartFromChange}
              />
            </DateField>
            <DateField>
              <Label text="掲載終了日時" emphasis="optional" />
              <DateInput
                type="datetime-local"
                value={endAt}
                onChange={onEndAtChange}
              />
            </DateField>
          </DateRow>
        </FieldGroup>

        <SubmitArea>
          <SubmitWrapper>
            <Button
              buttonType="primary"
              onClick={onSubmit}
              disabled={isSubmitting || !title || !body || !startFrom}
            >
              <Typography size={2} bold>
                作成する
              </Typography>
            </Button>
          </SubmitWrapper>
          <CancelDivider />
          <CancelLink
            onClick={() => {
              if (window.confirm("編集内容が破棄されます。よろしいですか？")) {
                onCancel();
              }
            }}
          >
            キャンセル
          </CancelLink>
        </SubmitArea>
      </ContentWrapper>
    </PageBody>
  );
}
